import {
  HHAlert,
  HHButton,
  HHStack,
  HHTextField,
} from '@hinge-health/react-component-library';
import { Autocomplete, TextField } from '@mui/material';
import { FormEvent, useState } from 'react';
import {
  ActionTypeDefinition,
  Configuration,
  CreateConfigurationInput,
} from '../domain/types';
import {
  ConfigurationActionDraft,
  toActionDrafts,
  toWireActions,
} from '../utils/configuration-action.utils';
import { parseJsonObject, stringifyJson } from '../utils/json.utils';
import { ActionBuilder } from './action-builder';
import { JsonEditor } from './json-editor';

export const CONFIGURATION_FORM_TEST_ID = 'pdg-configuration-form';
export const CONFIGURATION_FORM_NAME_TEST_ID =
  'pdg-configuration-form-name-input';
export const CONFIGURATION_FORM_PROVIDER_TEST_ID =
  'pdg-configuration-form-dataProvider-input';
export const CONFIGURATION_FORM_DEFINITION_TEST_ID =
  'pdg-configuration-form-definition';
export const CONFIGURATION_FORM_SUBMIT_TEST_ID =
  'pdg-configuration-form-submit-button';
export const CONFIGURATION_FORM_CANCEL_TEST_ID =
  'pdg-configuration-form-cancel-button';
export const CONFIGURATION_FORM_ERROR_TEST_ID =
  'pdg-configuration-form-error-alert';

export const NAME_REQUIRED_MESSAGE = 'Name is required.';
export const DATA_PROVIDER_REQUIRED_MESSAGE = 'Data provider is required.';

export const EMPTY_DEFINITION = '{}';

export const DEFINITION_LABEL = 'Rule definition (JSON)';
export const DEFINITION_HELPER_TEXT =
  'The Hyrule rule PDGS matches incoming files against.';

/** Matches `CreateConfigurationInput` — edit sends the same shape. */
export type ConfigurationFormValues = CreateConfigurationInput;

export interface ConfigurationFormProps {
  /** Seeds the form for edit; omitted for create. */
  configuration?: Configuration;
  dataProviders: string[];
  actionTypes: ActionTypeDefinition[];
  onSubmit: (values: ConfigurationFormValues) => void;
  onCancel: () => void;
  isSaving?: boolean;
  /** Mutation failure, rendered above the buttons. */
  error?: string | null;
  /** Read-only mode for operators without manage permission. */
  disabled?: boolean;
  submitLabel?: string;
}

interface FieldErrors {
  name: string | null;
  dataProvider: string | null;
  definition: string | null;
}

const NO_FIELD_ERRORS: FieldErrors = {
  name: null,
  dataProvider: null,
  definition: null,
};

/**
 * Create/edit form for a configuration.
 *
 * Both halves of the payload PDGS owns are edited through purpose-built
 * controls: `definition` through the raw JSON editor and `actions` through
 * the catalog-driven Action Builder. Field-level validation runs on submit
 * so the operator is never interrupted mid-edit.
 *
 * State is seeded once from `configuration`. Callers render the form only
 * after the configuration has loaded (and key it by id) rather than having
 * it resync itself, so in-progress edits can never be overwritten by a
 * refetch landing underneath them.
 */
export const ConfigurationForm = ({
  configuration,
  dataProviders,
  actionTypes,
  onSubmit,
  onCancel,
  isSaving = false,
  error = null,
  disabled = false,
  submitLabel = 'Save',
}: ConfigurationFormProps): JSX.Element => {
  const [name, setName] = useState(configuration?.name ?? '');
  const [dataProvider, setDataProvider] = useState(
    configuration?.dataProvider ?? '',
  );
  const [definitionText, setDefinitionText] = useState(
    configuration ? stringifyJson(configuration.definition) : EMPTY_DEFINITION,
  );
  const [actions, setActions] = useState<ConfigurationActionDraft[]>(
    toActionDrafts(configuration?.actions ?? []),
  );
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>(NO_FIELD_ERRORS);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    const definition = parseJsonObject(definitionText);
    const errors: FieldErrors = {
      name: name.trim() ? null : NAME_REQUIRED_MESSAGE,
      dataProvider: dataProvider.trim() ? null : DATA_PROVIDER_REQUIRED_MESSAGE,
      definition: definition.error,
    };

    setFieldErrors(errors);

    if (errors.name || errors.dataProvider || !definition.value) {
      return;
    }

    onSubmit({
      name: name.trim(),
      dataProvider: dataProvider.trim(),
      definition: definition.value,
      actions: toWireActions(actions, actionTypes),
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      data-testid={CONFIGURATION_FORM_TEST_ID}
    >
      <HHStack gap={3}>
        <HHStack direction="row" gap={2} flexWrap="wrap">
          <HHTextField
            hhVariant="variant-bypass"
            variant="outlined"
            size="small"
            label="Name"
            required
            value={name}
            disabled={disabled}
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name ?? undefined}
            onChange={(event): void => setName(event.target.value)}
            inputProps={{
              name: 'name',
              'aria-label': 'Name',
              'data-testid': CONFIGURATION_FORM_NAME_TEST_ID,
            }}
            sx={{ minWidth: 280 }}
          />
          {/*
            Free-solo: a brand new partner has no ingestions yet, so its
            provider will not be in `pdgDataProviders` until the first file
            lands — the operator has to be able to type it.
          */}
          <Autocomplete
            freeSolo
            options={dataProviders}
            value={dataProvider}
            disabled={disabled}
            size="small"
            sx={{ minWidth: 280 }}
            onChange={(_event, value): void => setDataProvider(value ?? '')}
            onInputChange={(_event, value): void => setDataProvider(value)}
            renderInput={(params): JSX.Element => (
              <TextField
                {...params}
                label="Data provider"
                required
                error={Boolean(fieldErrors.dataProvider)}
                helperText={fieldErrors.dataProvider ?? undefined}
                inputProps={{
                  ...params.inputProps,
                  name: 'dataProvider',
                  'data-testid': CONFIGURATION_FORM_PROVIDER_TEST_ID,
                }}
              />
            )}
          />
        </HHStack>

        <JsonEditor
          label={DEFINITION_LABEL}
          helperText={DEFINITION_HELPER_TEXT}
          name="definition"
          testId={CONFIGURATION_FORM_DEFINITION_TEST_ID}
          value={definitionText}
          disabled={disabled}
          error={fieldErrors.definition}
          onChange={(value): void => {
            setDefinitionText(value);
            setFieldErrors(previous => ({ ...previous, definition: null }));
          }}
        />

        <ActionBuilder
          actions={actions}
          actionTypes={actionTypes}
          disabled={disabled}
          onChange={setActions}
        />

        {error && (
          <HHAlert
            hhVariant="variant-bypass"
            variant="outlined"
            severity="error"
            data-testid={CONFIGURATION_FORM_ERROR_TEST_ID}
          >
            {error}
          </HHAlert>
        )}

        <HHStack direction="row" gap={2} justifyContent="flex-end">
          <HHButton
            hhVariant="variant-bypass"
            variant="outlined"
            type="button"
            disabled={isSaving}
            onClick={onCancel}
            data-testid={CONFIGURATION_FORM_CANCEL_TEST_ID}
          >
            Cancel
          </HHButton>
          <HHButton
            hhVariant="variant-bypass"
            variant="contained"
            type="submit"
            disabled={disabled || isSaving}
            data-testid={CONFIGURATION_FORM_SUBMIT_TEST_ID}
          >
            {isSaving ? 'Saving…' : submitLabel}
          </HHButton>
        </HHStack>
      </HHStack>
    </form>
  );
};
